import { FunctionComponent, useEffect, useState } from "react";
import { commafy } from "../utils/utils";
import {
  parseStoreHours,
  HourDetails,
  HoursByDay,
  daysOfTheWeek,
  createHourString,
} from "../utils/storeHours";
import "react-responsive-carousel/lib/styles/carousel.css";
import { STATES } from "../utils/states";
import { Link } from "gatsby";
import { StoreProductPivot, Store, Brands } from "../types.d.ts";
import filterProducts from "../utils/productFilter";
import StoreOpenClose from "./open-close";
import { Icon } from "../utils/Icons";
import { LocalBusiness, WithContext } from "schema-dts";
import { ThemeUICSSObject, ThemeUIStyleObject } from "theme-ui";
import { useSecureLinks } from "../../hooks/useSecureLinks";

interface Props {
  _openApp: Function;
  store: Store;
  brands?: Brands[];
  baseUrl: string;
}

const h3StylesCM: ThemeUICSSObject = {
  textTransform: "capitalize",
  fontFamily: "heading",
  color: "#000",
  fontWeight: "600",
  fontSize: "24px",
  width: "100%",
  borderBottom: "solid 2px #ccc",
  marginBottom: "10px",
};

const ConditionalLink: React.FC<{
  link: string | undefined;
  brands?: Brands[];
  children?: React.ReactNode;
}> = ({ link, brands, children }) => {
  const style = {
    width: "100%",
    paddingBottom: "15px",
    paddingTop: "15px",
    borderBottom: "none",
  } as ThemeUIStyleObject;
  if (link)
    return (
      <Link
        to={link}
        role="button"
        tabIndex={0}
        sx={{
          ...style,
          ":hover": {
            bg: "#fbfbfb",
          },
        }}
      >
        {children}
      </Link>
    );
  return <div sx={style}>{children}</div>;
};

const HoursSection: React.FC<HourDetails> = (props) => {
  const isOpen24Hours =
    (props.ranges || [])[0]?.deltas?.min == 0 &&
    (props.ranges || [])[0]?.deltas?.max == 10079;
  if (isOpen24Hours)
    return (
      <div>
        <strong>Open 24 hours</strong>
      </div>
    );
  return (
    <div>
      {(Object.values(daysOfTheWeek || []) as (keyof HoursByDay)[]).map(
        (day, i) => {
          const hours = props.days[day];
          return (
            <div
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                height: "25px",
                color: "#000",
                fontWeight: i == new Date().getDay() ? "bold" : "normal",
              }}
            >
              <div
                sx={{
                  textAlign: "left",
                }}
              >
                <p
                  sx={{
                    marginTop: "0px",
                    marginBottom: "0px",
                  }}
                >
                  {day}
                </p>
              </div>
              <div
                sx={{
                  borderBottom: "1px dotted #ccc !important",
                  flex: 1,
                }}
              ></div>
              <div
                sx={{
                  textAlign: "right",
                }}
              >
                <p
                  sx={{
                    marginTop: "0px",
                    marginBottom: "0px",
                  }}
                >
                  {hours
                    ? `${createHourString(
                        hours.openHour,
                        hours.openMin
                      )}-${createHourString(hours.closeHour, hours.closeMin)}`
                    : "Closed"}
                </p>
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

const StoreDetailsSection: FunctionComponent<Props> = (props: Props) => {
  const { store } = props;
  const hoursDetailed =
    store.hours == "Coming Soon" ? null : parseStoreHours(store.hours ?? "");

  const h3Styles = h3StylesCM;

  const phoneNumber = (store.phone ?? "")
    .slice(2)
    .toString()
    .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  const faxNumber = (store.fax ?? "")
    .slice(2)
    .toString()
    .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

  //per province regulation, do not show shortapp for manitoba
  const hideShortApp = store?.city?.state?.name?.toLowerCase() == "manitoba";
  const secureLinks = useSecureLinks();

  const isChangingToLd =
    store?.name === "LendDirect" ||
    store?.city?.state?.name === "New Brunswick";

  return isChangingToLd ? (
    <div
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        borderLeft: "none",
        borderRight: "none",
        borderBottom: "none",
        borderTop: "1px solid #e8e8e8",
        "& > div": {
          height: "100%",
          width: "100%",
        },
      }}
    >
      <div
        sx={{
          height: "100%",
          color: "#000",
        }}
      >
        <div>
          <div
            sx={{
              textAlign: "center",
            }}
          >
            <div
              sx={{
                bg: "#FF8600",
                py: "20px",
              }}
            >
              <svg
                sx={{ display: ["block"], margin: "0 auto" }}
                width="130"
                height="34"
                viewBox="0 0 302 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 36.612V1.05579H8.66442V28.8943H29.2825V36.612H0Z"
                  fill="#203466"
                />
                <path
                  d="M32.7891 36.612V1.05579H61.2244V8.32748H41.4535V14.8515H57.4377V21.8247H41.4535V29.3435H61.2725V36.6152H32.7891V36.612Z"
                  fill="#203466"
                />
                <path
                  d="M88.8117 36.612L72.4776 11.9633V36.612H64.8594V1.05579H76.014L88.4651 20.6278V1.05579H96.2823V36.612H88.8117Z"
                  fill="#203466"
                />
                <path
                  d="M99.7806 36.612V1.05579H112.976C115.864 1.05579 118.422 1.56282 120.646 2.57366C122.87 3.58772 124.747 4.93231 126.271 6.60744C127.799 8.28577 128.951 10.1919 129.731 12.3356C130.51 14.476 130.902 16.6774 130.902 18.9334C130.902 21.1893 130.488 23.5319 129.657 25.6563C128.826 27.7807 127.638 29.658 126.095 31.285C124.551 32.912 122.674 34.2052 120.469 35.1679C118.261 36.1307 115.765 36.612 112.973 36.612H99.7773H99.7806ZM112.626 29.1895C113.955 29.1895 115.206 28.9809 116.387 28.5669C117.565 28.153 118.586 27.5304 119.449 26.6993C120.312 25.8681 120.992 24.8316 121.49 23.5865C121.987 22.3414 122.237 20.8909 122.237 19.2286C122.237 17.4701 121.997 15.9169 121.515 14.5723C121.034 13.2277 120.37 12.1077 119.523 11.2124C118.675 10.3171 117.661 9.64319 116.484 9.19714C115.306 8.74787 114 8.52324 112.575 8.52324H108.442V29.1895H112.626Z"
                  fill="#203466"
                />
                <path
                  d="M156.283 12.2554C155.536 10.1085 154.435 8.19591 152.988 6.51438C151.54 4.83284 149.75 3.48825 147.622 2.47098C145.491 1.45692 143.046 0.94989 140.283 0.94989H126.375C127.408 1.72969 128.365 2.58971 129.225 3.53317C131.14 5.6351 132.607 8.06113 133.586 10.7375H138.566V5.12165L152.301 18.8564L138.566 32.5911V26.9785H133.611C133.57 27.0908 133.531 27.2031 133.489 27.3154C132.447 29.9853 130.932 32.3761 128.987 34.4234C128.246 35.2032 127.444 35.9253 126.584 36.5863H140.331C142.959 36.5863 145.331 36.105 147.446 35.1391C149.557 34.1731 151.348 32.8767 152.811 31.2465C154.274 29.6163 155.407 27.7358 156.206 25.605C157.005 23.4742 157.403 21.2278 157.403 18.866C157.403 16.5041 157.028 14.399 156.28 12.2521L156.283 12.2554Z"
                  fill="#017FAC"
                />
                <path
                  d="M160.668 36.4291V0.985229H166.03V36.4259H160.668V36.4291Z"
                  fill="#017FAC"
                />
                <path
                  d="M196.55 36.4291C196.085 35.7328 195.806 34.9979 195.706 34.2213C195.607 33.4447 195.523 32.6906 195.459 31.9621C195.36 30.839 195.228 29.6709 195.061 28.4611C194.894 27.2545 194.59 26.1538 194.143 25.159C193.697 24.1674 193.068 23.3555 192.257 22.7265C191.445 22.0975 190.363 21.7831 189.006 21.7831H175.355V36.4259H169.992V0.985229H188.059C189.648 0.985229 191.21 1.1168 192.751 1.38315C194.291 1.6495 195.671 2.15332 196.897 2.89782C198.119 3.64232 199.114 4.69489 199.875 6.0491C200.635 7.40653 201.017 9.15867 201.017 11.3119C201.017 13.4652 200.398 15.3585 199.156 16.8957C197.914 18.4328 196.204 19.4372 194.018 19.8993C195.34 20.2652 196.419 20.8268 197.247 21.5873C198.075 22.3478 198.736 23.2335 199.233 24.2444C199.73 25.252 200.083 26.3527 200.302 27.5433C200.517 28.7338 200.69 29.9597 200.821 31.2176C200.889 32.1772 200.969 33.1046 201.069 33.9967C201.168 34.8888 201.531 35.7007 202.16 36.4291H196.55ZM175.355 5.35595V17.5664H189.798C191.486 17.5664 192.882 17.0625 193.993 16.0517C195.1 15.0441 195.655 13.6128 195.655 11.758C195.655 10.5 195.456 9.45711 195.061 8.62918C194.663 7.80124 194.124 7.14981 193.447 6.66845C192.77 6.1903 191.984 5.85014 191.088 5.65118C190.193 5.45222 189.253 5.35274 188.258 5.35274H175.351L175.355 5.35595Z"
                  fill="#017FAC"
                />
                <path
                  d="M205.742 36.4291V0.985229H233.093V5.50357H211.104V15.9779H229.422V20.4962H211.104V31.914H233.144V36.4324H205.745L205.742 36.4291Z"
                  fill="#017FAC"
                />
                <path
                  d="M264.634 26.2019C263.858 27.524 262.905 28.66 261.781 29.6034C260.655 30.5469 259.407 31.2754 258.033 31.7888C256.66 32.3023 255.28 32.559 253.887 32.559C251.933 32.559 250.171 32.2028 248.602 31.4904C247.029 30.778 245.688 29.7864 244.581 28.5124C243.471 27.2384 242.62 25.7398 242.023 24.0197C241.426 22.2997 241.131 20.4127 241.131 18.3622C241.131 16.3116 241.436 14.6076 242.049 12.9517C242.662 11.299 243.528 9.86782 244.655 8.658C245.778 7.4514 247.129 6.50794 248.701 5.82763C250.274 5.15052 252.019 4.81036 253.938 4.81036C255.427 4.81036 256.859 5.04141 258.232 5.50672C259.606 5.97203 260.848 6.65877 261.955 7.56693C263.062 8.4783 263.989 9.60146 264.734 10.9428C265.154 11.7002 265.485 12.5249 265.732 13.417H270.982C270.651 11.8253 270.135 10.3524 269.422 9.00779C268.395 7.07274 267.105 5.43291 265.549 4.09474C263.992 2.75336 262.215 1.73609 260.212 1.04294C258.21 0.346577 256.101 0 253.884 0C251.169 0 248.704 0.487775 246.487 1.46332C244.27 2.43887 242.367 3.75458 240.778 5.41045C239.19 7.06632 237.957 9.00137 237.081 11.2188C236.205 13.4363 235.766 15.8013 235.766 18.3172C235.766 20.9968 236.196 23.4966 237.056 25.8136C237.916 28.1305 239.142 30.1394 240.73 31.8434C242.318 33.5474 244.221 34.8887 246.439 35.8643C248.656 36.8398 251.137 37.3276 253.884 37.3276C256.002 37.3276 258.046 36.9297 260.013 36.1371C261.98 35.3444 263.752 34.2501 265.324 32.8606C266.893 31.4711 268.193 29.8249 269.22 27.9219C269.9 26.6608 270.407 25.329 270.754 23.9331H265.642C265.382 24.7385 265.045 25.4991 264.628 26.2115L264.634 26.2019Z"
                  fill="#017FAC"
                />
                <path
                  d="M272.645 0.985229V5.80201H284.159V36.4291H289.521V5.80201H301.134V0.985229H272.645Z"
                  fill="#017FAC"
                />
              </svg>
              <h2
                sx={{
                  fontSize: "28px",
                  marginBottom: "5px",
                  fontWeight: "600",
                  fontFamily: "heading",
                }}
              >
                {store.crossStreets}
              </h2>
              <p
                sx={{
                  marginBottom: "0px",
                  marginTop: "0px",
                  fontSize: "0.95rem",
                }}
              >
                This is Now a LendDirect Branch
              </p>
            </div>

            <div
              sx={{
                marginBottom: "60px",
                px: ["20px", null, null, "58px"],
              }}
            >
              <div
                sx={{
                  display: "flex",
                  flexDirection: ["column"],
                  justifyContent: ["center"],
                  alignItems: "center",
                  marginBottom: "20px",
                  py: 2,
                  "& p": {
                    marginTop: "2.5px",
                    marginBottom: "2.5px",
                  },
                }}
              >
                <div
                  sx={{
                    marginTop: "10px",
                    mr: [0, null, null, 4],
                    fontSize: ["18px", null, null, "20px"],
                  }}
                >
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {store.address1}
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {store.city?.name +
                      ", " +
                      (STATES.find(
                        (state) => store.city?.state?.name == state.name
                      )?.abbr || "") +
                      " " +
                      store.zip}
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: ["16px", null, null, "18px"],
                      fontWeight: "600",
                      display: "none",
                    }}
                  >
                    <a
                      sx={{
                        variant: "a.default",
                        display: "inline-block",
                        mt: [3, null, null, 0],
                        fontFamily: "heading",
                        fontSize: "1.125rem",
                      }}
                      href={"https://lenddirect.ca"}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Visit LendDirect
                      <Icon
                        alt="External Link"
                        name="externalLinkAlt"
                        height={16}
                        width={16}
                        fill="brand.red"
                        sx={{
                          mx: 2,
                        }}
                      />
                    </a>
                  </p>
                </div>
                <div
                  sx={{
                    marginTop: "10px",
                    fontFamily: "heading",
                    fontSize: "1.125rem",
                  }}
                >
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"Phone: "}
                    <a
                      sx={{ variant: "a.default" }}
                      href={"tel:" + store.phone?.slice(1)}
                    >
                      {phoneNumber}
                    </a>
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"Fax: " + faxNumber}
                  </p>
                </div>
              </div>
              <hr
                sx={{
                  py: 3,
                  borderColor: "#ccc",
                }}
              />
              {
                <div
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <a
                    href={store.lendDirectUrl.url}
                    target="_blank"
                    sx={{
                      variant: "buttons.newPrimary",
                      py: "10px",
                      maxWidth: "250px",
                      mt: 2,
                      letterSpacing: ".5px",
                    }}
                  >
                    Visit LendDirect
                  </a>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        borderLeft: "none",
        borderRight: "none",
        borderBottom: "none",
        borderTop: "1px solid #e8e8e8",
        "& > div": {
          height: "100%",
          width: "100%",
        },
      }}
    >
      <div
        sx={{
          height: "100%",
          color: "#000",
        }}
      >
        <div>
          <div
            sx={{
              textAlign: "center",
            }}
          >
            <div
              sx={{
                bg: store.cashless ? "brand.skyBlue" : "white",
                mt: !store.cashless ? "30px" : "-1px",
                py: "20px",
              }}
            >
              {store.cashless && <p>Cashless</p>}
              <h2
                sx={{
                  fontSize: "28px",
                  marginBottom: "5px",
                  fontWeight: "600",
                  fontFamily: "heading",
                }}
              >
                {store.crossStreets}
              </h2>
              {hoursDetailed && (
                <h3
                  sx={{
                    textTransform: "none",
                    borderBottom: "none",
                    marginTop: "0px",
                    fontWeight: "400 !important",
                    marginBottom: "16px",
                  }}
                >
                  <StoreOpenClose store={store} hoursDetailed={hoursDetailed} />
                </h3>
              )}
            </div>
            <div sx={{ px: ["20px", null, null, "58px"] }}>
              {store.flashAlert && (
                <div
                  sx={{
                    backgroundColor: "#ffcccc",
                    borderRadius: "10px",
                    marginRight: ["0", "0", "0", "50px"],
                    marginLeft: ["0", "0", "0", "50px"],
                    marginTop: [3],
                  }}
                >
                  <p
                    sx={{
                      fontSize: "18px",
                      paddingTop: "15px",
                      paddingBottom: "15px",
                      paddingRight: "20px",
                      paddingLeft: "20px",
                    }}
                    dangerouslySetInnerHTML={{ __html: store.flashAlert }}
                  ></p>
                </div>
              )}
            </div>

            <div
              sx={{
                marginBottom: "60px",
                px: ["20px", null, null, "58px"],
              }}
            >
              <div
                sx={{
                  display: "flex",
                  flexDirection: ["column", null, null, "row"],
                  justifyContent: ["center", null, null, "space-between"],
                  alignItems: "center",
                  marginBottom: "20px",
                  "& p": {
                    marginTop: "2.5px",
                    marginBottom: "2.5px",
                  },
                }}
              >
                <div
                  sx={{
                    marginTop: "10px",
                    mr: [0, null, null, 4],
                    fontSize: ["18px", null, null, "20px"],
                  }}
                >
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {store.address1}
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {store.city?.name +
                      ", " +
                      (STATES.find(
                        (state) => store.city?.state?.name == state.name
                      )?.abbr || "") +
                      " " +
                      store.zip}
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: ["16px", null, null, "18px"],
                      fontWeight: "600",
                      display: "none",
                    }}
                  >
                    <a
                      sx={{
                        variant: "a.default",
                        display: "inline-block",
                        mt: [3, null, null, 0],
                        fontFamily: "heading",
                        fontSize: "1.125rem",
                      }}
                      href={
                        "https://maps.apple.com?saddr=Current+Location&daddr=@" +
                        store.gPS
                      }
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Get Directions
                      <Icon
                        alt="External Link"
                        name="externalLinkAlt"
                        height={16}
                        width={16}
                        fill="brand.red"
                        sx={{
                          mx: 2,
                        }}
                      />
                    </a>
                  </p>
                </div>
                <div
                  sx={{
                    marginTop: "10px",
                    fontFamily: "heading",
                    fontSize: "1.125rem",
                  }}
                >
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"Phone: "}
                    <a
                      sx={{ variant: "a.default" }}
                      href={"tel:" + store.phone?.slice(1)}
                    >
                      {phoneNumber}
                    </a>
                  </p>
                  <p
                    sx={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"Fax: " + faxNumber}
                  </p>
                </div>
              </div>
              {!hideShortApp && (
                <div
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* <button
                    sx={{
                      variant: "buttons.primary",
                      py: "10px",
                      maxWidth: "400px",
                      mt: 2,
                      letterSpacing: ".5px",
                    }}
                    onClick={() => props._openApp()}
                  >
                    Get Started
                  </button> */}
                  <a
                    href={secureLinks.loanApplication}
                    sx={{
                      variant: "buttons.newPrimary",
                      py: "10px",
                      maxWidth: "250px",
                      mt: 2,
                      letterSpacing: ".5px",
                    }}
                  >
                    Get Started
                  </a>
                </div>
              )}
            </div>
          </div>
          <div>
            <div sx={{ px: ["20px", null, null, "58px"] }}>
              <h3 sx={h3Styles}>Hours</h3>
              <div
                sx={{
                  marginBottom: "60px",
                }}
              >
                {hoursDetailed ? (
                  <HoursSection {...(hoursDetailed as HourDetails)} />
                ) : (
                  <strong>Coming Soon</strong>
                )}
              </div>
              <h3 sx={{ ...h3Styles, mb: 0 }}>Services</h3>
              <div
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  "& > a > div:not(:last-child)": {
                    borderBottom: "solid 1px #ccc",
                  },
                }}
              >
                {store.products
                  .sort(
                    (a: StoreProductPivot, b: StoreProductPivot) =>
                      a.orderIndex - b.orderIndex
                  )
                  .map((product: StoreProductPivot) => {
                    const stateProduct = (product.stateProduct || [])[0];
                    let url =
                      stateProduct?.url ||
                      (store.brand == Brands.Cashmoney
                        ? product.product?.cashMoneyUrl
                        : product.product?.speedyCashUrl) ||
                      undefined;
                    if (url && !url?.startsWith("/")) url = "/" + url;

                    return (
                      <ConditionalLink
                        link={url}
                        key={product.product?.productName}
                        brands={props.brands}
                      >
                        <div
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexShrink: 0,
                            minHeight: "50.4px",
                          }}
                        >
                          <div
                            sx={{
                              flexGrow: 10,
                              marginRight: "20px",
                            }}
                          >
                            <h5
                              sx={{
                                marginTop: "0px",
                                marginBottom: "0px",
                                fontWeight: "bold",
                                fontSize: "16px",
                                transition: "colors 0.3s ease-in",
                                ":hover": {
                                  color: "brand.red",
                                },
                              }}
                            >
                              {product.product?.productName}
                            </h5>
                            <p
                              sx={{
                                marginBottom: "0px",
                                marginTop: "0px",
                              }}
                            >
                              {stateProduct?.storeMaxAmount}
                            </p>
                          </div>
                          {url && (
                            <div
                              sx={{
                                textAlign: "center",
                              }}
                            >
                              <div
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  flexShrink: 0,
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                  "& img": {
                                    width: "28px",
                                  },
                                }}
                              >
                                <div
                                  sx={{
                                    width: "100px",
                                    textAlign: "center",
                                    px: "10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    fontSize: "24px",
                                    "& svg": {
                                      cursor: "pointer",
                                      color: "#00502f",
                                    },
                                  }}
                                >
                                  <Icon
                                    alt="Store Details"
                                    name="infoCircle"
                                    height={24}
                                    width={24}
                                    viewBox="0 0 30 30"
                                    tabIndex={0}
                                    fill="brand.green"
                                    sx={{
                                      mt: 1,
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </ConditionalLink>
                    );
                  })}
              </div>
            </div>
            {store.cashless && (
              <div
                sx={{
                  display: "flex",
                  flexShrink: 0,
                  justifyContent: "space-between",
                  bg: "brand.skyBlue",
                  mx: [3, null, null, "50px"],
                  borderRadius: 10,
                  p: "30px",
                  py: "20px",
                  marginBottom: 4,
                }}
              >
                <div>
                  <strong>Services not available at Cashless Branches</strong>
                  <p>Only available at standard Cash Money branches</p>
                  <ul
                    sx={{
                      listStyle: "inside",
                      pt: 3,
                    }}
                  >
                    <li>Money Orders</li>
                    <li>Western Union® Money Transfers</li>
                    <li>Funding or Payments with Cash</li>
                  </ul>
                </div>
                <div
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "50px",
                  }}
                >
                  <Icon
                    alt="Store Details"
                    name="infoCircle"
                    height={24}
                    width={24}
                    viewBox="0 0 30 30"
                    tabIndex={0}
                    fill="brand.green"
                    sx={{
                      mt: 1,
                    }}
                  />
                </div>
              </div>
            )}
            <div sx={{ px: ["20px", null, null, "58px"] }}>
              <h3
                sx={{
                  ...h3Styles,
                }}
              >
                Directions
              </h3>
              <p
                sx={{
                  pb: 20,
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: store.directionsText ?? "",
                  }}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreDetailsSection;
